import { useCallback } from 'react'
import type { FormEvent } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

import { communityChannel, communityPage } from '~publish/legacy/routes'

import type { CommunityTab } from '../../types'
import { isValidCommunityTab } from '../../util/typeGuards'

type UseTabNavigationParams = {
  channelId?: string
  availableTabs: CommunityTab[]
}

type UseTabNavigationReturn = {
  handleTabChange: (tab: CommunityTab) => void
  handleTabsChange: (tab: string | FormEvent<HTMLDivElement>) => void
}

/**
 * Shared hook for tab navigation in Comments pages
 * Handles navigation to either a specific channel's tab or the all channels tab
 */
export function useTabNavigation({
  channelId,
  availableTabs,
}: UseTabNavigationParams): UseTabNavigationReturn {
  const navigate = useNavigate()
  const location = useLocation()

  const handleTabChange = useCallback(
    (tab: CommunityTab): void => {
      if (channelId) {
        const route = communityChannel.getTabRoute({ channelId, tab })

        // Navigate to specific channel tab
        navigate(route)
      } else {
        // Navigate to all channels tab
        const newSearchParams = new URLSearchParams(location.search)
        newSearchParams.set('tab', tab)
        const basePath = communityPage.route
        navigate(`${basePath}?${newSearchParams.toString()}`)
      }
    },
    [navigate, channelId, location.search],
  )

  const handleTabsChange = useCallback(
    (tab: string | FormEvent<HTMLDivElement>): void => {
      if (
        typeof tab === 'string' &&
        isValidCommunityTab(tab) &&
        availableTabs.includes(tab)
      ) {
        handleTabChange(tab)
      }
    },
    [availableTabs, handleTabChange],
  )

  return {
    handleTabChange,
    handleTabsChange,
  }
}
